/** @jsxImportSource @emotion/react */
import { useState } from "react"
import { css, Box, Typography, Tabs, Tab } from "@mui/material"
import { withMyTheme } from "../../../../theme/theme"
import { mobileCss } from "../../../../util/isMobile"
import { useTranslation } from "react-i18next"
import { MenuIconType } from "../../../common/context/SharedFormTypes"

const GridStyle = withMyTheme(() => css`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
    padding: 0.5rem;

    ${mobileCss(`
        grid-template-columns: repeat(5, 1fr);
    `)}
`)

const IconCellStyle = (isSelected: boolean, iconColor?: string) => withMyTheme((theme) => css`
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    cursor: pointer;
    border: 2px solid ${isSelected ? theme.palette.primary.main : 'transparent'};
    background: ${isSelected ? theme.palette.primary.light + '30' : 'transparent'};
    transition: all 0.15s ease;
    font-size: 1.5rem;

    &:hover {
        background: ${theme.palette.primary.light}20;
    }

    svg {
        color: ${iconColor || theme.palette.text.primary};
        font-size: 1.4rem;
    }
`)

const TabsStyle = withMyTheme((theme) => css`
    min-height: unset;
    margin-bottom: 0.5rem;

    .MuiTab-root {
        min-height: 36px;
        font-family: ${theme.typography.body1.fontFamily};
        text-transform: none;
        font-size: 0.9rem;
    }

    .Mui-selected {
        color: ${theme.palette.primary.main} !important;
    }

    .MuiTabs-indicator {
        background-color: ${theme.palette.primary.main};
    }
`)

interface IconPickerProps {
    iconType: MenuIconType
    iconValue: string
    primaryColor: string
    emojis: string[]
    muiIcons: readonly string[]
    muiIconMap: Record<string, React.ReactElement>
    label: string
    emojiTabLabel: string
    muiTabLabel: string
    onSelect: (type: MenuIconType, value: string) => void
}

export const IconPicker = ({
    iconType, iconValue, primaryColor,
    emojis, muiIcons, muiIconMap,
    label, emojiTabLabel, muiTabLabel,
    onSelect
}: IconPickerProps) => {
    const [tab, setTab] = useState<MenuIconType>(iconType)

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                {label}
            </Typography>
            <Tabs
                css={TabsStyle}
                value={tab}
                onChange={(_, val) => setTab(val)}
            >
                <Tab label={emojiTabLabel} value="emoji" />
                <Tab label={muiTabLabel} value="mui" />
            </Tabs>

            {tab === 'emoji' && (
                <Box css={GridStyle}>
                    {emojis.map(emoji => (
                        <Box
                            key={emoji}
                            css={IconCellStyle(iconType === 'emoji' && iconValue === emoji)}
                            onClick={() => onSelect('emoji', emoji)}
                        >
                            {emoji}
                        </Box>
                    ))}
                </Box>
            )}

            {tab === 'mui' && (
                <Box css={GridStyle}>
                    {muiIcons.map(name => (
                        <Box
                            key={name}
                            css={IconCellStyle(iconType === 'mui' && iconValue === name, primaryColor)}
                            onClick={() => onSelect('mui', name)}
                        >
                            {muiIconMap[name]}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}
